import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import type { ArtRenderer, CoinView } from './model';
import {
  DEFAULT_QUEUE_MOTION,
  motionEase,
  type QueueMotion,
} from './queueMotion';
const RADIUS = 34,
  CIRCUMFERENCE = 2 * Math.PI * RADIUS,
  CHIP = 58;
type Row = { coin: CoinView; mode: 'enter' | 'idle' | 'exit' };
export function RoundQueue({
  coins,
  selectedId,
  now,
  art,
  motion = DEFAULT_QUEUE_MOTION,
  onSelect,
}: {
  coins: CoinView[];
  selectedId: string;
  now: number;
  art: ArtRenderer;
  motion?: QueueMotion;
  onSelect: (id: string) => void;
}) {
  const [rows, setRows] = useState<Row[]>(() =>
    coins.map((coin) => ({ coin, mode: 'idle' })),
  );
  const known = useRef(new Set(coins.map((coin) => coin.id)));
  const viewport = useRef<HTMLDivElement>(null);
  const selectedEl = useRef<HTMLButtonElement>(null);
  const ringDelay = useRef(new Map<string, string>());
  const shift = useRef(0);
  const [x, setX] = useState(0);
  const [armed, setArmed] = useState(false);
  const coinsRef = useRef(coins);
  coinsRef.current = coins;
  const ids = coins.map((coin) => coin.id).join();
  useEffect(() => {
    const live = new Map(coinsRef.current.map((coin) => [coin.id, coin]));
    const seen = known.current;
    setRows((current) => {
      const next: Row[] = [];
      const present = new Set<string>();
      for (const row of current) {
        const coin = live.get(row.coin.id);
        if (coin)
          next.push({
            coin,
            mode: row.mode === 'enter' ? 'enter' : 'idle',
          });
        else next.push({ ...row, mode: 'exit' });
        present.add(row.coin.id);
      }
      for (const coin of live.values()) {
        if (present.has(coin.id)) continue;
        next.push({ coin, mode: seen.has(coin.id) ? 'idle' : 'enter' });
      }
      return next.sort((a, b) => a.coin.start - b.coin.start);
    });
    for (const coin of coinsRef.current) seen.add(coin.id);
  }, [ids]);
  const leavingIds = rows
    .filter((row) => row.mode === 'exit')
    .map((row) => row.coin.id)
    .join();
  const enteringIds = rows
    .filter((row) => row.mode === 'enter')
    .map((row) => row.coin.id)
    .join();
  useEffect(() => {
    if (!leavingIds) return;
    const gone = new Set(leavingIds.split(','));
    const timer = window.setTimeout(() => {
      setRows((current) =>
        current.filter((row) => !gone.has(row.coin.id) || row.mode !== 'exit'),
      );
      for (const id of gone) {
        known.current.delete(id);
        ringDelay.current.delete(id);
      }
    }, motion.exitMs);
    return () => window.clearTimeout(timer);
  }, [leavingIds, motion.exitMs]);
  useEffect(() => {
    if (!enteringIds) return;
    const ids = new Set(enteringIds.split(','));
    const timer = window.setTimeout(() => {
      setRows((current) =>
        current.map((row) =>
          ids.has(row.coin.id) && row.mode === 'enter'
            ? { ...row, mode: 'idle' }
            : row,
        ),
      );
    }, motion.enterMs);
    return () => window.clearTimeout(timer);
  }, [enteringIds, motion.enterMs]);
  const liveSig = rows
    .filter((row) => row.mode !== 'exit')
    .map((row) => row.coin.id)
    .join();
  useLayoutEffect(() => {
    const view = viewport.current;
    if (!view || !selectedId) return;
    const align = () => {
      const target = view.clientWidth / 2 - CHIP / 2;
      if (Math.abs(target - shift.current) < 0.5) return;
      shift.current = target;
      setX(target);
    };
    align();
    let frame = 0;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(align);
    });
    observer.observe(view);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
    };
  }, [selectedId, liveSig, motion.tapeMs, rows]);
  useLayoutEffect(() => {
    if (armed || !selectedEl.current) return;
    const frame = requestAnimationFrame(() => setArmed(true));
    return () => cancelAnimationFrame(frame);
  }, [armed, x]);
  // Keep the active launch in state for switching back, but never duplicate it below.
  ringDelay.current.delete(selectedId);
  const alternatives = rows.filter((row) => row.coin.id !== selectedId);
  if (!alternatives.length) return null;
  return (
    <div
      className="round-queue"
      style={
        {
          '--queue-ms': motion.tapeMs + 'ms',
          '--queue-ease': motionEase(motion.tapeBounce),
          '--enter-ms': motion.enterMs + 'ms',
          '--exit-ms': motion.exitMs + 'ms',
          '--enter-scale': String(motion.enterScale),
          '--enter-blur': motion.enterBlur + 'px',
          '--exit-scale': String(motion.exitScale),
          '--exit-blur': motion.exitBlur + 'px',
          '--hover-ms': motion.hoverMs + 'ms',
          '--hover-scale': String(motion.hoverScale),
          '--hover-spin': motion.hoverSpin + 'deg',
          '--hover-ax': (motion.hoverAxis ?? 'y') === 'x' ? '1' : '0',
          '--hover-ay': (motion.hoverAxis ?? 'y') === 'y' ? '1' : '0',
          '--hover-az': (motion.hoverAxis ?? 'y') === 'z' ? '1' : '0',
        } as CSSProperties
      }
    >
      <div ref={viewport} className="queue-viewport">
        <ul
          className={'queue-tape' + (armed ? ' is-armed' : '')}
          style={{ transform: 'translateX(' + x + 'px)' }}
          aria-label="Open prediction windows"
        >
          {alternatives.map((row, index) => {
            const distance = index;
            const far = Math.min(1, distance / 3);
            const scale = 1 - (1 - motion.tapeScale) * far;
            const blur = motion.tapeBlur * far;
            const total = Math.max(1, row.coin.closesAt - row.coin.start);
            let delay = ringDelay.current.get(row.coin.id);
            if (delay == null) {
              delay =
                -Math.min(Math.max(0, now - row.coin.start), total) + 'ms';
              ringDelay.current.set(row.coin.id, delay);
            }
            return (
              <li
                key={row.coin.id}
                className={'queue-item is-' + row.mode}
                style={{
                  transform: 'scale(' + scale + ')',
                  filter: blur ? 'blur(' + blur + 'px)' : undefined,
                }}
              >
                <button
                  type="button"
                  ref={index === 0 ? selectedEl : undefined}
                  className="queue-chip"
                  disabled={row.mode === 'exit'}
                  title="Show this launch"
                  onClick={() => onSelect(row.coin.id)}
                >
                  {art(row.coin, 'queue-art')}
                  <svg viewBox="0 0 74 74" aria-hidden="true" focusable="false">
                    <circle
                      className="queue-track"
                      cx="37"
                      cy="37"
                      r={RADIUS}
                    />
                    <circle
                      className="queue-progress"
                      cx="37"
                      cy="37"
                      r={RADIUS}
                      strokeDasharray={CIRCUMFERENCE}
                      style={
                        {
                          '--ring-ms': total + 'ms',
                          '--ring-delay': delay,
                        } as CSSProperties
                      }
                    />
                  </svg>
                  <span className="sr-only">
                    {row.coin.name} (${row.coin.symbol})
                    {row.coin.simulated && ' · simulated'}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
